/**
 * WP-2 H5 — Shared listener/action lifecycle bookkeeping.
 *
 * Both the DOM leg (ListenerBinder) and the bus leg (useStepReactions) key
 * their "already fired?" checks on the SAME storage keys so a listener that
 * fired on one leg is not re-fired on the other. Lifecycle semantics:
 *
 *   unlimited | always | page → never suppressed (re-fires freely)
 *   session   | once_per_session → suppressed for the sessionStorage lifetime
 *   once | lifetime | once_ever → suppressed for the localStorage lifetime
 *   once_per_day → suppressed until the next calendar day (UTC)
 *
 * `once` is the backoffice Listeners-builder value for "fire once, ever"; it is
 * an alias of the canonical `once_ever` so an authored listener card behaves as
 * the author expects.
 */

export function getListenerKey(stepId: string, listenerId: string): string {
  return `funnel_listener_${stepId}_${listenerId}`;
}

/**
 * Per-listener lifecycle wins over the step-level default. Authors set lifecycle
 * on a listener card (`config.lifecycle`); fall back to the step-level trigger
 * lifecycle only when the listener does not specify one.
 */
export function resolveListenerLifecycle(config: unknown, fallback: string): string {
  const lifecycle = (config as { lifecycle?: unknown } | undefined)?.lifecycle;
  return typeof lifecycle === 'string' && lifecycle.trim() !== '' ? lifecycle : fallback;
}

export function hasListenerFired(stepId: string, listenerId: string, lifecycle: string): boolean {
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') return false;
  const key = getListenerKey(stepId, listenerId);
  try {
    if (lifecycle === 'lifetime' || lifecycle === 'once_ever' || lifecycle === 'once')
      return localStorage.getItem(key) === 'fired';
    if (lifecycle === 'session' || lifecycle === 'once_per_session') return sessionStorage.getItem(key) === 'fired';
    if (lifecycle === 'once_per_day') {
      const today = new Date().toISOString().slice(0, 10);
      return localStorage.getItem(key) === today;
    }
  } catch {
    return false;
  }
  return false;
}

export function markListenerFired(stepId: string, listenerId: string, lifecycle: string): void {
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') return;
  const key = getListenerKey(stepId, listenerId);
  try {
    if (lifecycle === 'lifetime' || lifecycle === 'once_ever' || lifecycle === 'once')
      localStorage.setItem(key, 'fired');
    else if (lifecycle === 'session' || lifecycle === 'once_per_session') sessionStorage.setItem(key, 'fired');
    else if (lifecycle === 'once_per_day') localStorage.setItem(key, new Date().toISOString().slice(0, 10));
  } catch {
    /* ignore — best-effort suppression */
  }
}
